import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';
import { authedFetch } from '@/lib/auth';
import { onboardingSetupProgress } from '@/lib/onboardingProgress';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SetupProgressPill — small topbar widget that surfaces onboarding
 * progress for partially-onboarded customers. Currently the dashboard
 * hard-gates these users to /dashboard/onboarding, so the pill is a
 * visual reinforcement on that page itself and a one-tap nudge anywhere
 * else if/when the gate is relaxed in the future (e.g. for trial-
 * extending Business customers who skip ETC Usage Inquiry Service (MEISAI) temporarily).
 *
 * Hidden when:
 *   - user is admin (handled by parent)
 *   - user has onboarding_completed=true (handled by parent)
 *   - meisai is connected AND has at least one card (handled by parent)
 *
 * Props:
 *   user — the current user object (must include onboarding_completed)
 */
export default function SetupProgressPill({ user, onboardingComplete }) {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const [meisai, setMeisai] = useState(null);
  const [cardsCount, setCardsCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancel = false;
    (async () => {
      try {
        const [s, c] = await Promise.all([
          authedFetch(`${API}/api/user/meisai/status`, { credentials: 'include' }).then(r => r.ok ? r.json() : null).catch(() => null),
          authedFetch(`${API}/api/cards`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).catch(() => []),
        ]);
        if (cancel) return;
        setMeisai(s);
        setCardsCount(Array.isArray(c) ? c.length : 0);
      } finally {
        if (!cancel) setLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, [user]);

  if (!user) return null;
  if (user.onboarding_completed === true) return null;
  if (onboardingComplete) return null;
  if (!loaded) return null; // avoid flicker

  // Authoritative 5-step onboarding model — shared with OnboardingPage via
  // lib/onboardingProgress so the topbar pill and the on-page panel can NEVER
  // show contradicting numbers (previously pill "3/4" vs panel "2/5"). The
  // meisai_connect + cards_confirm pair is complete once MEISAI is connected
  // AND at least one ETC card is present (matches the panel's meisaiStepDone,
  // which owes card verification while credentials exist but no card is yet
  // confirmed).
  const meisaiConnected = !!(meisai && meisai.has_credentials);
  const hasCard = cardsCount > 0;
  const meisaiStepDone = meisaiConnected && hasCard;
  const { done, total, pct } = onboardingSetupProgress({ meisaiStepDone });

  return (
    <Link
      to="/dashboard/onboarding"
      className="hidden md:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-fuchsia-50 to-violet-50 border border-fuchsia-200 hover:border-fuchsia-300 hover:from-fuchsia-100 hover:to-violet-100 transition-all group"
      data-testid="topbar-setup-progress-pill"
      title={ja ? 'セットアップを続ける' : 'Resume setup'}
    >
      <div className="flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-fuchsia-600 flex-shrink-0" strokeWidth={2.4} />
        <span className="text-[11px] font-bold text-fuchsia-900 whitespace-nowrap">
          {ja ? `セットアップ ${done}/${total}` : `Setup ${done}/${total}`}
        </span>
      </div>
      {/* Mini bar */}
      <div className="w-12 h-1 rounded-full bg-fuchsia-200/70 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-violet-500 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ArrowRight className="w-3 h-3 text-fuchsia-700 group-hover:translate-x-0.5 transition-transform flex-shrink-0" strokeWidth={2.4} />
    </Link>
  );
}
